import styles from "./Hero.module.scss";
import { StartPageType } from "@/types/ModuleOptionsType";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Startseite } from "@/wp/types/acf-header-types";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import Split from "@/components/_primitives/Split/Split";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import { passACFLink } from "@/utils/passACFLink";
import { getVideoData } from "@/getData/getVideoData";
import { Post } from "@/wp/types/client";
import router from "next/router";
import { useState, useEffect } from "react";
import { Headline } from "@/components/Headline/Headline";
import { Button } from "@/components/Button/Button";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";

interface HeroProps {
  header: Header_Startseite["header_startseite"];
  startPageOptions: StartPageType;
  navbarHasBackground: boolean;
}

export const Hero = ({
  header,
  startPageOptions,
  navbarHasBackground,
}: HeroProps) => {
  const {
    media,
    title,
    overline,
    subline,
    background_color_class,
    row_direction_class,
  } = header;
  const {
    image_fullhd,
    image_desktop,
    image_tablet,
    image_mobile,
    type,
    autoplay,
  } = media;

  const {
    imageheader: {
      vertical_align: imageheaderVerticalAlign,
      text_horizontal_align: imageheaderTextHorizontalAlign,
      text_vertical_align: imageheaderTextVerticalAlign,
      headline_gradient: imageheaderHeadlineGradient,
      image_height: imageheaderImageHeight,
    },
    button_type,
    show_overline_subline,
    second_button_type,
    two_buttons,
    splitheader: {
      image_height: splitheaderImageHeight,
      media_format: splitheaderMediaFormat,
      header_width: splitheaderWidth,
      text_vertical_align: splitheaderTextVerticalAlign,
      text_width_with_spacer: splitheaderTextWidthWithSpacer,
      row_direction_class_mobile: splitHeaderVerticalDirection,
    },
    show_extended_link,
  } = startPageOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const splitHeaderDirection: "row" | "row-reverse" =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  const contentJSX = (
    <div
      className={`${styles["text-col"]} ${header.type !== "splitheader" ? "p-0" : ""}`}
    >
      <div className={`${styles["header-info-wrapper"]}`}>
        {title && (
          <Headline
            headline_html_tag={"h1-hero"}
            headline={title}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            disableAnimation
          />
        )}

        <div className={`content`}>
          {header.teasertext.title && (
            <span className="title">{header.teasertext.title}</span>
          )}
          {header.teasertext.content && (
            <div
              className="intro-text"
              dangerouslySetInnerHTML={{
                __html: header.teasertext.content,
              }}
            />
          )}
          <div className={`content ${styles["links-wrapper"]}`}>
            {show_extended_link ? (
              <>
                {header.link_extended && (
                  <ExtendedLink
                    extendedLink={header.link_extended}
                    btnType={
                      button_type
                        ? button_type
                        : header.link_extended.type === "email"
                          ? "secondary"
                          : "primary"
                    }
                  />
                )}
                {two_buttons && header.link_2_extended && (
                  <ExtendedLink
                    extendedLink={header.link_2_extended}
                    btnType={
                      button_type
                        ? button_type
                        : header.link_2_extended.type === "email"
                          ? "secondary"
                          : "primary"
                    }
                  />
                )}
              </>
            ) : (
              <>
                {header.link && header.link.url && (
                  <Button
                    type={button_type ?? "primary"}
                    {...passACFLink(header, "link")}
                  >
                    {header.link.title}
                  </Button>
                )}
                {two_buttons && header.link_2 && header.link_2.url && (
                  <Button
                    type={second_button_type ?? "secondary"}
                    {...passACFLink(header, "link_2")}
                  >
                    {header.link_2.title}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);

  useEffect(() => {
    if (media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);
  const mediaJSX =
    type === "image" ? (
      <MediaItemResponsive
        image_fullhd={image_fullhd}
        image_desktop={image_desktop}
        image_tablet={image_tablet}
        image_mobile={image_mobile}
        sizes="(max-width: 450px) 50vw, 100vw"
        quality={90}
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
      />
    ) : type === "video" &&
      videoData &&
      videoData.acf.header_videos.video_type === "upload" ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );

  switch (header.type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""}`}
          header={<></>}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"normal-frame"}
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${!navbarHasBackground ? "hero" : ""}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"normal-frame"}
          contentHorizontalAlignment={imageheaderTextHorizontalAlign ?? "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""}`}
          headerFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          contentFrame={"narrow-frame"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            <Split
              first={mediaJSX}
              firstClassName={"min-height-acceptor-desktop"}
              second={contentJSX}
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              verticalDirection={
                splitHeaderVerticalDirection === "is-column-normal-mobile"
                  ? "column-reverse"
                  : "column"
              }
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
